import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import { trackPageView } from '../../utils/eventTracking'
import jsPDF from 'jspdf'
import { 
  DollarSign, 
  Calendar, 
  CreditCard, 
  CheckCircle, 
  Clock, 
  XCircle,
  Download,
  Filter,
  Search,
  TrendingUp,
  AlertCircle
} from 'lucide-react'
import toast from 'react-hot-toast'

const CustomerPayments = () => {
  const { getPayments } = useApi()
  const { userId } = useUserId()
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all') // all, premium, claim, refund
  const [statusFilter, setStatusFilter] = useState('all') // all, completed, pending, failed
  const [searchTerm, setSearchTerm] = useState('')

  // Track page view
  useEffect(() => {
    trackPageView('Customer Payments', { user_id: userId })
  }, [userId])

  useEffect(() => {
    if (userId) {
      loadPayments()
    }
  }, [userId])

  const loadPayments = async () => {
    try {
      setLoading(true)
      const paymentsData = await getPayments()
      
      // Filter payments for logged-in user by customer_id
      const userPayments = paymentsData?.filter(payment => payment.customer_id === userId) || []
      setPayments(userPayments)
    } catch (err) {
      console.error('Error loading payments:', err)
      setError('Failed to load payment history')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    if (!amount) return '₹0'
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
      case 'COMPLETED':
      case 'SUCCESS':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'PENDING':
      case 'PROCESSING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'FAILED':
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
      case 'COMPLETED':
      case 'SUCCESS':
        return <CheckCircle className="h-4 w-4" />
      case 'PENDING':
      case 'PROCESSING':
        return <Clock className="h-4 w-4" />
      case 'FAILED':
      case 'REJECTED':
        return <XCircle className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const getPaymentTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'PREMIUM_PAYMENT':
        return 'bg-blue-100 text-blue-800'
      case 'CLAIM_PAYMENT':
      case 'CLAIM_SETTLEMENT':
        return 'bg-green-100 text-green-800'
      case 'REFUND':
      case 'ADJUSTMENT':
        return 'bg-purple-100 text-purple-800'
      case 'CASHBACK':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDownloadReceipt = (payment) => {
    try {
      // Create PDF receipt
      const pdf = new jsPDF('p', 'mm', 'a4')
      
      // Header
      pdf.setFontSize(20)
      pdf.setTextColor(59, 130, 246) // Blue
      pdf.text('Payment Receipt', 105, 20, { align: 'center' })
      
      // Company Info
      pdf.setFontSize(10)
      pdf.setTextColor(100, 100, 100)
      pdf.text('YourInsurance - Bengaluru, Karnataka', 105, 28, { align: 'center' })
      pdf.text('insurance.devrev@gmail.com', 105, 33, { align: 'center' })
      
      // Line
      pdf.setDrawColor(200, 200, 200)
      pdf.line(20, 38, 190, 38)
      
      // Payment Details
      pdf.setFontSize(12)
      pdf.setTextColor(0, 0, 0)
      pdf.setFont(undefined, 'bold')
      pdf.text('Payment Information', 20, 48)
      
      pdf.setFont(undefined, 'normal')
      pdf.setFontSize(10)
      
      let y = 58
      const lineHeight = 6
      
      // Add payment details
      pdf.text(`Payment Number:`, 20, y)
      pdf.text(`${payment.payment_number || 'N/A'}`, 80, y)
      y += lineHeight
      
      pdf.text(`Payment Type:`, 20, y)
      pdf.text(`${payment.payment_type?.replace(/_/g, ' ') || 'N/A'}`, 80, y)
      y += lineHeight
      
      pdf.text(`Payment Date:`, 20, y)
      pdf.text(`${formatDate(payment.payment_date)}`, 80, y)
      y += lineHeight
      
      pdf.text(`Payment Status:`, 20, y)
      if (payment.payment_status === 'PAID') {
        pdf.setTextColor(34, 197, 94) // Green for PAID
      } else {
        pdf.setTextColor(234, 179, 8) // Yellow for others
      }
      pdf.text(`${payment.payment_status || 'N/A'}`, 80, y)
      pdf.setTextColor(0, 0, 0)
      y += lineHeight
      
      pdf.text(`Payment Method:`, 20, y)
      pdf.text(`${payment.payment_method?.replace(/_/g, ' ') || 'N/A'}`, 80, y)
      y += lineHeight
      
      pdf.text(`Transaction ID:`, 20, y)
      pdf.text(`${payment.payment_transaction_id || 'N/A'}`, 80, y)
      y += lineHeight + 4
      
      // Amount section
      pdf.setDrawColor(200, 200, 200)
      pdf.line(20, y, 190, y)
      y += 8
      
      pdf.setFontSize(14)
      pdf.setFont(undefined, 'bold')
      pdf.text(`Payment Amount:`, 20, y)
      pdf.setTextColor(34, 197, 94) // Green
      // Format amount without extra spaces
      const amountStr = `Rs ${parseFloat(payment.payment_amount).toLocaleString('en-IN')}`
      pdf.text(amountStr, 80, y)
      pdf.setTextColor(0, 0, 0)
      y += lineHeight + 8
      
      // Beneficiary Info
      if (payment.beneficiary_name) {
        pdf.setDrawColor(200, 200, 200)
        pdf.line(20, y, 190, y)
        y += 8
        
        pdf.setFontSize(12)
        pdf.setFont(undefined, 'bold')
        pdf.text('Beneficiary Details', 20, y)
        y += 8
        
        pdf.setFont(undefined, 'normal')
        pdf.setFontSize(10)
        
        pdf.text(`Name:`, 20, y)
        pdf.text(`${payment.beneficiary_name}`, 80, y)
        y += lineHeight
        
        if (payment.beneficiary_bank) {
          pdf.text(`Bank:`, 20, y)
          pdf.text(`${payment.beneficiary_bank}`, 80, y)
          y += lineHeight
        }
        
        if (payment.beneficiary_account) {
          pdf.text(`Account:`, 20, y)
          pdf.text(`${payment.beneficiary_account}`, 80, y)
          y += lineHeight
        }
      }
      
      // Footer
      y = 280
      pdf.setFontSize(8)
      pdf.setTextColor(150, 150, 150)
      pdf.text('This is a computer-generated receipt. No signature required.', 105, y, { align: 'center' })
      pdf.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 105, y + 4, { align: 'center' })
      
      // Download
      pdf.save(`Receipt_${payment.payment_number}.pdf`)
      toast.success('Receipt downloaded successfully!')
      
    } catch (error) {
      console.error('Error generating receipt:', error)
      toast.error('Failed to generate receipt')
    }
  }

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    // Type filter
    if (filter !== 'all') {
      const typeMatch = payment.payment_type?.toLowerCase().includes(filter.toLowerCase())
      if (!typeMatch) return false
    }

    // Status filter
    if (statusFilter !== 'all' && payment.payment_status?.toUpperCase() !== statusFilter.toUpperCase()) {
      return false
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return (
        payment.payment_number?.toLowerCase().includes(searchLower) ||
        payment.payment_reference?.toLowerCase().includes(searchLower) ||
        payment.payment_transaction_id?.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  // Calculate statistics from actual Supabase data
  const totalPaid = payments
    .filter(p => p.payment_status?.toUpperCase() === 'PAID' || p.payment_status?.toUpperCase() === 'COMPLETED')
    .reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0)

  const pendingPayments = payments.filter(p => 
    p.payment_status?.toUpperCase() === 'PENDING' || 
    p.payment_status?.toUpperCase() === 'PROCESSING'
  ).length
  
  const completedPayments = payments.filter(p => 
    p.payment_status?.toUpperCase() === 'PAID' || 
    p.payment_status?.toUpperCase() === 'COMPLETED'
  ).length

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment history...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Payments</h3>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Payment History</h1>
        <p className="text-sm text-gray-600">View all your premium and claim payments</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Total Paid</p>
              <p className="text-2xl font-bold text-green-900 mt-1">{formatCurrency(totalPaid)}</p>
            </div>
            <div className="bg-green-200 p-3 rounded-full">
              <TrendingUp className="h-6 w-6 text-green-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800">Completed Payments</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{completedPayments}</p>
            </div>
            <div className="bg-blue-200 p-3 rounded-full">
              <CheckCircle className="h-6 w-6 text-blue-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800">Pending Payments</p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">{pendingPayments}</p>
            </div>
            <div className="bg-yellow-200 p-3 rounded-full">
              <Clock className="h-6 w-6 text-yellow-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by payment number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="premium">Premium Payments</option>
              <option value="claim">Claim Payments</option>
              <option value="refund">Refunds</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Payments Found</h3>
          <p className="text-gray-500">
            {searchTerm || filter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No payment history available yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPayments.map((payment) => (
            <div
              key={payment.payment_id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${getPaymentTypeColor(payment.payment_type)}`}>
                    <DollarSign className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {payment.payment_number || 'N/A'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {payment.payment_type?.replace(/_/g, ' ') || 'Payment'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(payment.payment_amount)}
                  </p>
                  <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border mt-2 ${getStatusColor(payment.payment_status)}`}>
                    {getStatusIcon(payment.payment_status)}
                    <span>{payment.payment_status || 'Unknown'}</span>
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                {payment.payment_method && (
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Method:</span>
                    <span className="font-medium text-gray-900">
                      {payment.payment_method.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}

                {payment.payment_completed_date && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(payment.payment_completed_date)}
                    </span>
                  </div>
                )}

                {payment.payment_transaction_id && (
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium text-gray-900 font-mono text-xs">
                      {payment.payment_transaction_id}
                    </span>
                  </div>
                )}
              </div>

              {payment.payment_beneficiary_name && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Beneficiary</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {payment.payment_beneficiary_name}
                      </span>
                    </div>
                    {payment.payment_beneficiary_bank && (
                      <div>
                        <span className="text-gray-600">Bank:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {payment.payment_beneficiary_bank}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Download Receipt Button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => handleDownloadReceipt(payment)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Receipt</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Section */}
      {filteredPayments.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Payment Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-blue-700">Total Transactions:</p>
              <p className="font-semibold text-blue-900 text-lg">{filteredPayments.length}</p>
            </div>
            <div>
              <p className="text-blue-700">Completed:</p>
              <p className="font-semibold text-blue-900 text-lg">
                {filteredPayments.filter(p => 
                  p.payment_status?.toUpperCase() === 'PAID' || 
                  p.payment_status?.toUpperCase() === 'COMPLETED'
                ).length}
              </p>
            </div>
            <div>
              <p className="text-blue-700">Total Amount:</p>
              <p className="font-semibold text-blue-900 text-lg">
                {formatCurrency(
                  filteredPayments
                    .filter(p => 
                      p.payment_status?.toUpperCase() === 'PAID' || 
                      p.payment_status?.toUpperCase() === 'COMPLETED'
                    )
                    .reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0)
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerPayments

